import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Shield, Loader2 } from "lucide-react";

const DEFAULT_TEMPLATE = `# Location tracking notice

We use your device's location to automatically start and end shifts when
you arrive at or leave a work site. This helps us keep your timesheet
accurate without you having to remember to clock in.

- Location is only recorded while you are on shift.
- Location is stored with your time entry as proof of attendance.
- You can adjust or reject any auto-generated entry from the app.

If you have any questions, contact your administrator.`;

function markdownToHtml(md) {
  // Extremely small, safe-ish renderer — we intentionally do NOT ship a full
  // markdown library because operator-authored notices are already displayed
  // to their own workers. Escape HTML then apply a few block/inline rules.
  const esc = (md || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const lines = esc.split("\n");
  const out = [];
  let inList = false;
  for (const line of lines) {
    if (/^\s*[-*]\s+/.test(line)) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${line.replace(/^\s*[-*]\s+/, "")}</li>`);
    } else {
      if (inList) { out.push("</ul>"); inList = false; }
      if (/^# /.test(line)) out.push(`<h1>${line.slice(2)}</h1>`);
      else if (/^## /.test(line)) out.push(`<h2>${line.slice(3)}</h2>`);
      else if (line.trim() === "") out.push("");
      else out.push(`<p>${line}</p>`);
    }
  }
  if (inList) out.push("</ul>");
  return out.join("\n");
}


export default function PrivacyNoticePage() {
  const [state, setState] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [markdown, setMarkdown] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tenant/privacy");
      setState(data);
      setEnabled(Boolean(data.enabled));
      setMarkdown(data.privacy_notice_markdown || "");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/tenant/privacy", {
        enabled,
        privacy_notice_markdown: markdown || null,
      });
      setState(data);
      toast.success(`Privacy notice saved (v${data.privacy_notice_version})`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (state == null) {
    return (
      <div className="grid h-64 place-items-center text-muted-foreground" data-testid="privacy-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const version = state.privacy_notice_version || 0;

  return (
    <div className="space-y-6" data-testid="admin-privacy-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Workspace settings</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Privacy notice</h1>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          Workers must accept this notice before geofence auto-clock can record
          their location. Every save that changes the text or enabled flag
          bumps the version — existing acceptances become stale and workers
          are re-prompted the next time they open the app.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-secondary" /> Geofence consent
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" data-testid="privacy-version-badge">v{version}</Badge>
            <Label htmlFor="privacy-enabled" className="text-sm text-muted-foreground">Enabled</Label>
            <Switch
              id="privacy-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
              data-testid="privacy-enabled-toggle"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="privacy-md">Notice text (Markdown)</Label>
            <Textarea
              id="privacy-md"
              rows={14}
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              placeholder="Explain what you collect, when, and why…"
              className="mt-2 font-mono text-sm"
              data-testid="privacy-markdown-textarea"
            />
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMarkdown(DEFAULT_TEMPLATE)}
                data-testid="privacy-load-template"
              >
                Load sample template
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                data-testid="privacy-save-btn"
                className="ml-auto"
              >
                {saving ? "Saving…" : "Save & bump version"}
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-accent/40 p-4">
            <div className="mb-3 text-xs font-medium uppercase tracking-widest text-muted-foreground">
              Worker preview
            </div>
            {markdown ? (
              <div
                className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-primary prose-p:text-foreground prose-li:text-foreground"
                data-testid="privacy-preview"
                dangerouslySetInnerHTML={{ __html: markdownToHtml(markdown) }}
              />
            ) : (
              <p className="text-sm italic text-muted-foreground">
                No notice yet. Write one above or load the sample template.
              </p>
            )}
          </div>

          {state.updated_at && (
            <p className="text-xs text-muted-foreground" data-testid="privacy-last-saved">
              Last saved: <span className="font-mono">{state.updated_at}</span>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
